import _ from "lodash";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { getGuestType } from "../components/activities/ActivityBookingProcess/helpers";
import {
  requiredFieldsFlights,
  requiredInternationalFielssFls,
} from "../components/flights/FlightBookingProcess/helpers";

function getGuestRequiredKeys({ requiredFields }) {
  return Object.entries(requiredFields)
    .filter(
      ([key, value]) =>
        !["lead_guest", "service_type", "group_leader", "room_leader"].includes(
          key
        )
    )
    .filter(([key, value]) => value === true)
    .map(([key, value]) => key);
}

function validateGuest({ guest, requiredFields, nonValidKeyMessage }) {
  const msgs = [];
  const requiredKeys = getGuestRequiredKeys({ requiredFields });

  requiredKeys.forEach((field) => {
    if (!_.get(guest, field)) {
      msgs.push(
        <span>
          <strong>{_.startCase(field)}</strong> {nonValidKeyMessage}
        </span>
      );
    }
  });

  return msgs;
}

export function validateGuests({
  adults,
  children,
  adultRequiredFields,
  childRequiredFields,
  leadAdultRequiredFields,
  srv_type,
}) {
  const leadAdult = adults.find((guest) => guest.group_leader);
  const nonLeadAdults = adults.filter((guest) => !guest.group_leader);

  return [
    ...validateGuest({
      guest: leadAdult,
      requiredFields:
        leadAdultRequiredFields.service_type === srv_type
          ? leadAdultRequiredFields
          : {},
      nonValidKeyMessage: (
        <>
          is required for the <strong>Lead Guest.</strong>
        </>
      ),
    }),
    ..._.flatten(
      nonLeadAdults.map((guest, idx) =>
        validateGuest({
          guest,
          requiredFields:
            adultRequiredFields.service_type === srv_type
              ? adultRequiredFields
              : {},
          nonValidKeyMessage: (
            <>
              is required for <strong>Adult {idx + 1}.</strong>
            </>
          ),
        })
      )
    ),
    ..._.flatten(
      children.map((guest, idx) =>
        validateGuest({
          guest,
          requiredFields:
            childRequiredFields.service_type === srv_type
              ? childRequiredFields
              : {},
          nonValidKeyMessage: (
            <>
              is required for <strong>Child {idx + 1}.</strong>
            </>
          ),
        })
      )
    ),
  ];
}

export const validateAccSearchForm = ({
  destination_code = [],
  checkIn,
  checkOut,
}) => {
  const msgs = [];

  if (!destination_code.length) {
    msgs.push("A destination must be specified.");
  }

  if (DateTime.fromISO(checkIn).invalid || DateTime.fromISO(checkOut).invalid) {
    msgs.push("Check In/Out dates must be specified.");
  }

  return msgs;
};

function validateActivityGuest({ form, guestForm, guest_type, idx, t }) {
  const validity = Object.entries(guestForm)
    .filter(([key, value]) => form[key].required)
    .map(([key, value], idx) => {
      const valid = Boolean(value);
      if (!valid) {
        toast.error(
          `Guest ${getGuestType({ guest_type, t })}: ${
            form[key].label
          } is required.`,
          {
            duration: 6000,
          }
        );
      }

      return valid;
    });

  const guestIsValid = validity.every((v) => v);
  return guestIsValid;
}

export function validateActivityGuests({ guests, forms, t }) {
  const adults = guests.filter((g) => g.guest_type === "ADT");
  const children = guests.filter((g) => g.guest_type === "CHD");
  const infants = guests.filter((g) => g.guest_type === "INF");

  const validAdults = adults.filter((guest, idx) => {
    const form = forms.find((f) => f.guest_uuid === guest.guest_uuid);
    return validateActivityGuest({
      form,
      guestForm: guest.form,
      guest_type: "ADT",
      idx,
      t,
    });
  });

  const validChildren = children.filter((guest, idx) => {
    const form = forms.find((f) => f.guest_uuid === guest.guest_uuid);
    return validateActivityGuest({
      form,
      guestForm: guest.form,
      guest_type: "CHD",
      idx,
      t,
    });
  });

  const validInfants = infants.filter((guest, idx) => {
    const form = forms.find((f) => f.guest_uuid === guest.guest_uuid);
    return validateActivityGuest({
      form,
      guestForm: guest.form,
      guest_type: "INF",
      idx,
      t,
    });
  });
  return (
    validAdults.length === adults.length &&
    validChildren.length === children.length &&
    validInfants.length === infants.length
  );
}

export function validateFlightGuests({ guests, isInternational, t }) {
  var { adults: adultsReqFlds, children: childrenReqFlds } =
    requiredFieldsFlights;

  if (isInternational) {
    adultsReqFlds = [
      ...adultsReqFlds,
      ...requiredInternationalFielssFls.adults,
    ];
    childrenReqFlds = [
      ...childrenReqFlds,
      ...requiredInternationalFielssFls.children,
    ];
  }

  const adults = guests.filter((guest) => guest.guest_type === "ADT");
  const children = guests.filter((guest) => guest.guest_type === "CHD");

  if (!adults.length) {
    return false;
  }
  const fieldsStatus = [];
  adults.forEach((adt, idx) => {
    adultsReqFlds.forEach((reqFld) => {
      const valid = Boolean(adt[reqFld]);
      if (!valid) {
        toast.error(
          `Guest ${getGuestType({
            guest_type: "ADT",
            t,
          })}: ${_.capitalize(reqFld.replaceAll("_", " "))} is required.`,
          {
            duration: 6000,
          }
        );
      }
      fieldsStatus.push(valid);
    });
  });
  children.forEach((adt, idx) => {
    childrenReqFlds.forEach((reqFld) => {
      const valid = Boolean(adt[reqFld]);
      if (!valid) {
        toast.error(
          `Guest ${getGuestType({
            guest_type: "CHD",
            t,
          })}: ${_.capitalize(reqFld.replaceAll("_", " "))} is required.`,
          {
            duration: 6000,
          }
        );
      }
      fieldsStatus.push(valid);
    });
  });

  return fieldsStatus.length ? fieldsStatus.every((p) => p) : false;
}
